import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ArrowLeft, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";

interface AmountSelectorProps {
  amount: number;
  isCustomAmount: boolean;
  customAmount: string;
  onAmountChange: (value: string) => void;
  onCustomAmountChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBackToPreset: () => void;
}

export function AmountSelector({
  amount,
  isCustomAmount,
  customAmount,
  onAmountChange,
  onCustomAmountChange,
  onBackToPreset,
}: AmountSelectorProps) {
  return (
    <div className="flex gap-2">
      {isCustomAmount ? (
        <motion.div
          className="relative"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.2 }}
        >
          <Input
            type="text"
            placeholder="Amount"
            value={customAmount}
            onChange={onCustomAmountChange}
            className="w-28 shrink-0 bg-[#ffffff]/5 border-[#ffffff]/10 text-white h-10 placeholder:text-[#ffffff]/30 pr-8 transition-all focus:ring-2 focus:ring-[#9945FF]"
          />
          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[#ffffff]/50 text-sm">
            SOL
          </span>
        </motion.div>
      ) : (
        <Select
          value={isCustomAmount ? "custom" : amount.toString()}
          onValueChange={onAmountChange}
        >
          <SelectTrigger className="w-full h-12 bg-[#ffffff]/5 border-[#ffffff]/10 text-white hover:bg-[#ffffff]/10 transition-colors">
            <SelectValue placeholder="Select amount" />
          </SelectTrigger>
          <SelectContent className="bg-[#111111] border-[#ffffff]/10 text-white">
            {[1, 2, 5].map((value) => (
              <SelectItem
                key={value}
                value={value.toString()}
                className="hover:bg-[#ffffff]/5 focus:bg-[#ffffff]/5 cursor-pointer"
              >
                <div className="flex items-center gap-2">
                  <div className="bg-gradient-to-r from-[#9945FF] to-[#00D18C] bg-clip-text text-transparent font-bold text-lg">
                    {value}
                  </div>
                  <span className="text-[#ffffff]/70 font-medium">SOL</span>
                  <div className="ml-auto text-xs text-[#ffffff]/30">
                    {value === 1
                      ? "Minimum"
                      : value === 5
                      ? "Maximum"
                      : "Standard"}
                  </div>
                </div>
              </SelectItem>
            ))}
            <SelectItem
              value="custom"
              className="hover:bg-[#ffffff]/5 focus:bg-[#ffffff]/5 cursor-pointer border-t border-[#ffffff]/10"
            >
              <div className="flex items-center gap-2">
                <div className="bg-gradient-to-r from-[#9945FF] to-[#00D18C] bg-clip-text text-transparent font-bold text-lg">
                  <Sparkles className="h-5 w-5" />
                </div>
                <span className="text-[#ffffff]/70 font-medium">
                  Custom Amount
                </span>
                <div className="ml-auto text-xs text-[#ffffff]/30">
                  Enter your own
                </div>
              </div>
            </SelectItem>
          </SelectContent>
        </Select>
      )}
      {isCustomAmount && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.2 }}
        >
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-10 w-10 bg-[#ffffff]/5 hover:bg-[#ffffff]/10 transition-colors"
                  onClick={onBackToPreset}
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">
                <p>Back to preset amounts</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </motion.div>
      )}
    </div>
  );
}
